import type { Rng } from '../rng.js'
import type { TeddyKey } from '../teddies.js'
import { RIDDLE_SKILLS, riddleSkillOf, type RiddleShape } from './riddle-skills.js'
import {
  allAssignments,
  clueKey,
  holderOf,
  matches,
  solve,
  type Assignment,
  type Clue,
  type ClueKind,
} from './riddle-solve.js'
import { RIDDLE_CAST, RIDDLE_CATEGORIES, type RiddleCategory } from './riddle-things.js'
import type { RiddleQuestion } from './riddle-words.js'

/**
 * Where a riddle comes from.
 *
 * The rule this file exists to obey, in one line: **no clue is ever
 * constructed to be true.** Every clue that could be written is enumerated
 * blind, and then `matches` — the same function the solver uses — is asked
 * which of them happen to be true of the deal.
 *
 * The alternative, writing a clue that says what you already know, is how a
 * generator and a reader come to disagree about what a sentence means while
 * every test passes. That shipped once already, in Turtley's Trolley, as a
 * question that asked "how much more did you spend?" of the smaller basket.
 */

export interface Riddle {
  /** In seating order, front first. Given, drawn, and never deduced. */
  readonly teddies: readonly TeddyKey[]
  readonly categories: readonly RiddleCategory[]
  /** The truth. Never sent to a browser mid-round. */
  readonly deal: Assignment
  readonly clues: readonly Clue[]
  readonly question: RiddleQuestion
  /** The seat that answers it, counting from the front, from one — as she presses it. */
  readonly answer: number
  /** The hardest kind of clue in it, which is what the riddle is really testing. */
  readonly skillKey: string
}

/**
 * Every clue of the allowed kinds that could be written about this row.
 *
 * Blind: it takes the shape and the vocabulary, and never sees the deal. A few
 * hundred at the largest shape, so enumerating them all costs nothing and
 * removes any chance of the pool being biased toward the answer.
 */
export function allPossibleClues(
  categories: readonly RiddleCategory[],
  teddies: number,
  kinds: readonly ClueKind[],
): Clue[] {
  const clues: Clue[] = []
  const seats = Array.from({ length: teddies }, (_, seat) => seat)
  const allows = (kind: ClueKind): boolean => kinds.includes(kind)

  for (const category of categories) {
    const things = category.things.slice(0, teddies)

    for (const thing of things) {
      const about = { category: category.key, thing: thing.key } as const

      for (const seat of seats) {
        if (allows('direct')) clues.push({ kind: 'direct', seat, ...about })
        if (allows('negative')) clues.push({ kind: 'negative', seat, ...about })
        if (allows('neighbour')) clues.push({ kind: 'neighbour', seat, ...about })
        if (allows('side')) {
          clues.push({ kind: 'side', seat, behind: 'thing', ...about })
          clues.push({ kind: 'side', seat, behind: 'teddy', ...about })
        }
      }

      if (allows('end')) {
        clues.push({ kind: 'end', end: 'front', ...about })
        clues.push({ kind: 'end', end: 'back', ...about })
      }

      if (allows('either')) {
        for (const first of seats) {
          for (const second of seats) {
            // Unordered: "Sky or Bluey" and "Bluey or Sky" are one clue said
            // twice, and a pool holding both would weight it double.
            if (first < second) clues.push({ kind: 'either', seats: [first, second], ...about })
          }
        }
      }

      if (allows('link')) {
        for (const other of categories) {
          // Across two categories only. A clue linking a thing to something in
          // its own category would either be trivially true or impossible, and
          // both read as nonsense.
          if (other.key === category.key) continue

          for (const otherThing of other.things.slice(0, teddies)) {
            clues.push({
              kind: 'link',
              ...about,
              otherCategory: other.key,
              otherThing: otherThing.key,
            })
          }
        }
      }
    }
  }

  return clues
}

/**
 * Whether a clue hands her the answer without any reading of the row.
 *
 * Only two kinds can: a `direct` clue about the thing she is asked about names
 * the teddy outright, and an `end` clue about it names a seat, which — because
 * the row is drawn and she can see it — is the same thing.
 *
 * `neighbour` is deliberately not here. It can pin a thing to one seat when the
 * teddy it names is at an end, but she has to look at the row to find that out,
 * and that is a deduction rather than a giveaway.
 */
export function givesAwayAnswer(clue: Clue, question: RiddleQuestion): boolean {
  if (clue.category !== question.category || clue.thing !== question.thing) return false

  return clue.kind === 'direct' || clue.kind === 'end'
}

/**
 * A riddle with exactly one answer, in as few clues as say it.
 *
 * There is no retry loop, and that is not an accident. The pool always
 * contains the direct clues for every thing but the one she is asked about,
 * and those pin the deal on their own — the last one falls out because the
 * deal is a bijection. So the greedy loop below cannot fail to reach a single
 * surviving assignment, and a seed is never a lottery ticket that a sweep of
 * three hundred cannot clear.
 */
export function buildRiddle(shape: RiddleShape, rng: Rng): Riddle {
  const categories = RIDDLE_CATEGORIES.slice(0, shape.categories)
  // The shuffle is the seating. She can see it, so it is one less thing to
  // hold in her head — making the row unknown would multiply the search by
  // another factorial and ask her to carry two hypotheses at once.
  const teddies = rng.shuffle(RIDDLE_CAST).slice(0, shape.teddies)

  const deal: Assignment = Object.fromEntries(
    categories.map((category) => [
      category.key,
      rng.shuffle(category.things.slice(0, shape.teddies).map((thing) => thing.key)),
    ]),
  )

  const askedCategory = rng.pick(categories)
  const question: RiddleQuestion = {
    category: askedCategory.key,
    thing: rng.pick(askedCategory.things.slice(0, shape.teddies)).key,
  }

  const truthful = allPossibleClues(categories, shape.teddies, shape.kinds)
    .filter((clue) => matches(clue, deal))
    .filter((clue) => !givesAwayAnswer(clue, question))

  /*
   * The one clue that names somebody outright, at the levels that get one.
   *
   * Chosen here rather than inside the loop because it is needed twice: it
   * goes first, so the riddle is built around it, and it is held back from the
   * minimising step, which would otherwise notice that the rest of the clues
   * already say enough and drop the very thing it is there to provide.
   *
   * That leaves the clue set not strictly minimal at these levels, which is
   * exactly right and is why `foothold` and `redundant` turn off together:
   * a level that wants a foothold is a level that wants spare ways in, and the
   * minimality test skips both for the same reason.
   */
  const foothold = shape.foothold
    ? truthful.filter((clue) => clue.kind === 'direct').slice(0, 1)
    : []

  const chosen = narrowToOne(categories, shape, rng, truthful, foothold)
  const needed = dropWhatIsNotNeeded(categories, shape, chosen, foothold)
  const clues = withRedundant(needed, truthful, shape.redundant, rng)

  return {
    teddies,
    categories,
    deal,
    clues,
    question,
    // From one, because that is the key she presses.
    answer: holderOf(deal, question.category, question.thing) + 1,
    skillKey: skillPractised(categories, shape.teddies, clues),
  }
}

/** Clues chosen one at a time until a single deal is left standing. */
function narrowToOne(
  categories: readonly RiddleCategory[],
  shape: RiddleShape,
  rng: Rng,
  truthful: readonly Clue[],
  foothold: readonly Clue[],
): Clue[] {
  const chosen: Clue[] = []
  let surviving = allAssignments(categories, shape.teddies)

  for (const clue of inOrderOfPreference(truthful, rng, foothold)) {
    if (surviving.length <= 1) break

    const next = surviving.filter((assignment) => matches(clue, assignment))
    /*
     * A clue that rules nothing out is a sentence she reads for nothing.
     *
     * Strictly this is work saved rather than a guarantee: the minimising step
     * below would drop such a clue anyway, since removing it leaves the same
     * one deal standing. It is here because doing it now costs a comparison
     * and doing it later costs a full solve. No test fails without it, and
     * that is the honest reason why.
     */
    if (next.length === surviving.length) continue

    chosen.push(clue)
    surviving = next
  }

  return chosen
}

/**
 * The order clues are offered to the greedy loop.
 *
 * Interesting kinds first and `direct` last, so a riddle is made of deduction
 * and falls back on being told only when it must. The foothold is the
 * exception: at the levels that have one, a single direct clue goes first so
 * there is somewhere to put your finger.
 */
function inOrderOfPreference(
  truthful: readonly Clue[],
  rng: Rng,
  foothold: readonly Clue[],
): Clue[] {
  const shuffled = rng.shuffle(truthful)
  const direct = shuffled.filter((clue) => clue.kind === 'direct')
  const rest = shuffled.filter((clue) => clue.kind !== 'direct')

  // Starts empty, and the foothold goes first, so the foothold survives and
  // its twin further down the `direct` list is the one dropped. Seeding this
  // with the foothold's own key — which is what it did first — filtered out
  // the very clue it was there to guarantee.
  const seen = new Set<string>()

  return [...foothold, ...rest, ...direct].filter((clue) => {
    const key = clueKey(clue)
    if (seen.has(key)) return false
    seen.add(key)

    return true
  })
}

/**
 * Drops every clue the riddle can do without.
 *
 * In reverse order of how they were added, so that — since `direct` clues are
 * offered last and therefore added last — the clue most likely to give the
 * game away is the one most likely to go.
 *
 * The foothold is held back from this entirely. It is redundant almost by
 * definition once the rest of the clues are in, and dropping it is how a
 * beginner's riddle came out as nothing but "did not".
 */
function dropWhatIsNotNeeded(
  categories: readonly RiddleCategory[],
  shape: RiddleShape,
  chosen: readonly Clue[],
  foothold: readonly Clue[],
): Clue[] {
  const protectedKeys = new Set(foothold.map(clueKey))
  let kept = [...chosen]

  for (const clue of [...chosen].reverse()) {
    if (protectedKeys.has(clueKey(clue))) continue

    const without = kept.filter((candidate) => clueKey(candidate) !== clueKey(clue))
    if (without.length === kept.length) continue

    if (solve(categories, shape.teddies, without).length === 1) kept = without
  }

  return kept
}

/** Spare ways in, for the levels that still want them. */
function withRedundant(
  needed: readonly Clue[],
  truthful: readonly Clue[],
  howMany: number,
  rng: Rng,
): Clue[] {
  if (howMany <= 0) return [...needed]

  const already = new Set(needed.map(clueKey))
  const spare = rng
    .shuffle(truthful.filter((clue) => !already.has(clueKey(clue))))
    .slice(0, howMany)

  // Shuffled together, so a spare clue is not always the last one she reads.
  return rng.shuffle([...needed, ...spare])
}

/**
 * What the riddle is recorded as practising.
 *
 * Not the hardest kind of clue on the page — the hardest kind it could not
 * have been solved without. Those are different, and the difference moves her
 * levels for the wrong reason.
 *
 * The easy levels are dealt spare clues on purpose, and the spares are drawn
 * from the whole pool. So a riddle could show a "Sky or Bluey took the pear"
 * that its own facts and eliminations already made unnecessary, be recorded as
 * practice at narrowing it to two, and move that level up. Or down, for a
 * wrong answer that had nothing to do with it — a level falling because she
 * misread a different clue entirely. Roughly one riddle in four at level five,
 * measured.
 *
 * Reading a clue is not the same as needing it, and only needing it is
 * practice. Taking it from the clues the *generator* thought were needed is
 * not enough either: a spare fact can make a set of easy clues sufficient on
 * its own, so the riddle in her hands is easier than the one it was built as.
 *
 * So this asks the finished riddle directly. Working down from the hardest
 * kind present: is what is easier than this enough on its own? The first kind
 * for which the answer is no is the difficulty she actually met.
 */
function skillPractised(
  categories: readonly RiddleCategory[],
  teddies: number,
  clues: readonly Clue[],
): string {
  const tierOf = (clue: Clue): number => riddleSkillOf(clue.kind).unlocksAt
  const tiers = [...new Set(clues.map(tierOf))].sort((a, b) => b - a)

  for (const tier of tiers) {
    const easier = clues.filter((clue) => tierOf(clue) < tier)
    // Still more than one deal standing, so something at this level of
    // difficulty was doing work.
    if (solve(categories, teddies, easier).length === 1) continue

    /*
     * Of the kinds at this tier, the last one listed that is actually here.
     * `direct` and `negative` share a tier, and RIDDLE_SKILLS lists them in
     * that order deliberately: being told a fact and ruling one out are the
     * same level of unlock and not the same act, and ruling out is the one
     * worth crediting when a riddle contains both.
     */
    const here = RIDDLE_SKILLS.filter(
      (skill) => skill.unlocksAt === tier && clues.some((clue) => clue.kind === skill.kind),
    )

    const chosen = here[here.length - 1]
    if (chosen) return chosen.key
  }

  // Only reachable for a riddle with no clues at all, which the greedy loop
  // cannot produce: it takes a clue every time it narrows, and three teddies
  // are never narrowed to one by nothing.
  return riddleSkillOf('direct').key
}
